import { useAuthStore } from "@/store";
import axios, { AxiosError } from "axios";
import type { InternalAxiosRequestConfig } from "axios";

const api = axios.create({
  baseURL: `${import.meta.env.VITE_SERVER_URL}/api`,
  withCredentials: true,
  // Tells the API which cookie identifies us. Both portals share a cookie jar
  // under .univdiam.com, so without this the server has to guess.
  headers: { "X-Client-Portal": "admin" },
});

const REFRESH_URL = "/admin/refresh";

// Single in-flight refresh shared by every request that 401s at once, so a
// burst of parallel calls triggers exactly one /refresh, then all replay.
let isRefreshing = false;
let pendingQueue: Array<(ok: boolean) => void> = [];

const flushQueue = (ok: boolean) => {
  pendingQueue.forEach((cb) => cb(ok));
  pendingQueue = [];
};

const forceLogout = () => {
  const { user, logout } = useAuthStore.getState();
  if (user) {
    logout();
    window.location.href = "/login";
  }
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;
    const status = error.response?.status;

    // Only handle a first 401 that isn't the refresh call itself.
    if (
      status !== 401 ||
      !original ||
      original._retry ||
      original.url?.includes(REFRESH_URL)
    ) {
      return Promise.reject(error);
    }

    original._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push((ok) =>
          ok ? resolve(api(original)) : reject(error)
        );
      });
    }

    isRefreshing = true;
    try {
      await api.post(REFRESH_URL);
      isRefreshing = false;
      flushQueue(true);
      return api(original);
    } catch (refreshError) {
      isRefreshing = false;
      flushQueue(false);
      forceLogout();
      return Promise.reject(refreshError);
    }
  }
);

/**
 * Force a token refresh from outside the response interceptor.
 *
 * The Socket.IO handshake authenticates with the access token cookie and is
 * checked exactly once, at connect time. When that cookie has expired the
 * server rejects the handshake inside a middleware -- and Socket.IO does NOT
 * retry a middleware rejection on its own, so the socket stays dead for the
 * rest of the page session. Nothing else on the page necessarily 401s, so the
 * interceptor above may never run and never mint a new cookie. This is how the
 * socket store asks for one.
 *
 * Shares the same in-flight guard and queue as the interceptor, so a burst of
 * 401s and a dead socket cannot trigger two concurrent refreshes.
 *
 * Resolves false rather than throwing, and deliberately does NOT force a
 * logout: a refresh can fail because the network is down, and dropping the
 * user out of the app for that would be worse than a socket that stays offline
 * until the next attempt.
 */
export const refreshSession = async (): Promise<boolean> => {
  if (isRefreshing) {
    return new Promise<boolean>((resolve) => {
      pendingQueue.push(resolve);
    });
  }

  isRefreshing = true;
  try {
    await api.post(REFRESH_URL);
    isRefreshing = false;
    flushQueue(true);
    return true;
  } catch {
    isRefreshing = false;
    flushQueue(false);
    return false;
  }
};

export default api;
